/**
 * Everything the guide does once it is in the room: idle, gesture, blink, speak
 * and arrive. Driven from the render loop via `update(delta)`.
 *
 * All timing is frame-accumulated rather than `setTimeout`/`setInterval`. Wall
 * clock timers drift against a 72–90 Hz XR loop, and keep firing while the
 * session is hidden, so the guide gestures at an empty headset.
 */
import { LoopOnce, MathUtils, Quaternion, Vector3, type AnimationAction } from 'three';

import type { LoadedAvatar } from './loadAvatar.js';
import type { AvatarProfile } from './profile.js';

/** Frame-rate independent decay toward a target. */
const damp = (current: number, target: number, rate: number, delta: number): number =>
  MathUtils.lerp(current, target, 1 - Math.exp(-rate * delta));

/** A human blink is roughly this long; slower reads as a wince. */
const BLINK_DURATION_SECONDS = 0.14;

export class AvatarController {
  private idleAction?: AnimationAction;
  private gestureAction?: AnimationAction;
  private idleTimer = 0;
  private idleInterval = 0;

  private mouthOpen = 0;
  private mouthPhase = 0;
  private speaking = false;

  private blinkTimer = 0;
  private blinkInterval = 0;
  private blinkProgress = -1;

  private entranceElapsed = Number.POSITIVE_INFINITY;
  private readonly baseOpacity = new Map<unknown, number>();

  private readonly lookTarget = new Vector3();
  private readonly selfPosition = new Vector3();
  private readonly worldFacing = new Quaternion();

  constructor(
    private readonly avatar: LoadedAvatar,
    private readonly profile: AvatarProfile,
  ) {
    for (const material of avatar.materials) {
      const value = (material as { opacity?: number }).opacity;
      this.baseOpacity.set(material, typeof value === 'number' ? value : 1);
    }
    this.idleInterval = this.nextIdleInterval();
    this.blinkInterval = this.nextBlinkInterval();
  }

  /**
   * The model's world rotation, which is its yaw toward the viewer.
   *
   * Read by the light rig, which lives at the level root rather than under the
   * guide and so has to be told this rather than inheriting it. Returns a
   * shared scratch object — read it, do not keep it.
   */
  get facing(): Quaternion {
    return this.avatar.scene.getWorldQuaternion(this.worldFacing);
  }

  /** Scale the model so it stands `targetHeightMeters` tall. */
  applyScale(): void {
    const scale = this.profile.targetHeightMeters / this.avatar.naturalHeight;
    this.avatar.scene.scale.setScalar(scale);
  }

  /** Begin the fade-in. Call once, at the moment of placement. */
  startEntrance(): void {
    this.entranceElapsed = 0;
    for (const material of this.avatar.materials) {
      const mutable = material as { transparent?: boolean; opacity?: number };
      mutable.transparent = true;
      mutable.opacity = 0;
    }
    this.avatar.scene.visible = true;
    this.playIdle();
    this.playGesture(this.profile.clips.greeting);
  }

  /** Drive the mouth. True while the guide's reply is still revealing. */
  setSpeaking(speaking: boolean): void {
    this.speaking = speaking;
  }

  /** Play a one-shot clip, cross-fading from whatever is running. */
  playGesture(clipName: string | undefined): void {
    if (!clipName || !this.avatar.mixer) return;
    const clip = this.avatar.clips.get(clipName) ?? this.findClipInsensitive(clipName);
    if (!clip) return;

    const action = this.avatar.mixer.clipAction(clip);
    action.reset();
    action.setLoop(LoopOnce, 1);
    action.clampWhenFinished = true;

    const from = this.gestureAction ?? this.idleAction;
    if (from && from !== action) {
      from.crossFadeTo(action, this.profile.transitionSeconds, true);
    }
    action.play();
    this.gestureAction = action;
    // Don't interrupt a deliberate gesture with a random idle variation.
    this.idleTimer = 0;
  }

  update(delta: number, cameraPosition: Vector3): void {
    this.avatar.mixer?.update(delta);
    this.updateEntrance(delta);
    this.updateIdle(delta);
    this.updateMouth(delta);
    this.updateBlink(delta);
    this.faceViewer(cameraPosition);
  }

  dispose(): void {
    this.avatar.mixer?.stopAllAction();
  }

  // ── internals ───────────────────────────────────────────────────────────

  private playIdle(): void {
    if (!this.avatar.mixer) return;
    const clip = this.avatar.clips.get(this.profile.clips.idle) ?? [...this.avatar.clips.values()][0];
    if (!clip) return;
    const action = this.avatar.mixer.clipAction(clip);
    action.reset().fadeIn(this.profile.transitionSeconds).play();
    this.idleAction = action;
  }

  private updateEntrance(delta: number): void {
    if (this.entranceElapsed >= this.profile.entranceSeconds) return;
    this.entranceElapsed += delta;

    const t = Math.min(this.entranceElapsed / this.profile.entranceSeconds, 1);
    const eased = 1 - (1 - t) ** 2;

    for (const material of this.avatar.materials) {
      const mutable = material as { transparent?: boolean; opacity?: number };
      const base = this.baseOpacity.get(material) ?? 1;
      mutable.opacity = base * eased;
    }

    if (t >= 1) {
      // Restore the authored transparency flags — a material that was genuinely
      // translucent must stay translucent, and one that wasn't should go back
      // to opaque rather than paying for blending forever.
      for (const material of this.avatar.materials) {
        const mutable = material as { transparent?: boolean; opacity?: number };
        const base = this.baseOpacity.get(material) ?? 1;
        mutable.opacity = base;
        mutable.transparent = base < 1;
      }
    }
  }

  private updateIdle(delta: number): void {
    if (!this.avatar.mixer || this.avatar.clips.size === 0) return;

    // A gesture that has finished hands control back to the idle loop.
    if (this.gestureAction && !this.gestureAction.isRunning()) {
      this.gestureAction = undefined;
      this.idleAction?.reset().fadeIn(this.profile.transitionSeconds).play();
      this.idleTimer = 0;
      this.idleInterval = this.nextIdleInterval();
    }
    if (this.gestureAction) return;

    this.idleTimer += delta;
    if (this.idleTimer < this.idleInterval) return;
    this.idleTimer = 0;
    this.idleInterval = this.nextIdleInterval();

    const pool = this.profile.clips.idlePool.filter((name) => this.avatar.clips.has(name));
    const pick = pool[Math.floor(Math.random() * pool.length)];
    if (pick) this.playGesture(pick);
  }

  /**
   * Mouth movement is deliberately not viseme-accurate — it reads correctly at
   * conversational distance and costs nothing. Three incommensurate sines give
   * a non-repeating wobble; a plain sine looks mechanical within a sentence.
   */
  private updateMouth(delta: number): void {
    if (this.avatar.mouth.length === 0) return;

    if (this.speaking) {
      this.mouthPhase += delta * 9;
      const wobble =
        Math.sin(this.mouthPhase) * 0.3 +
        Math.sin(this.mouthPhase * 2.3) * 0.2 +
        Math.sin(this.mouthPhase * 3.7) * 0.15;
      const target = MathUtils.clamp((wobble + 0.65) * 0.5 * this.profile.morphs.mouthIntensity, 0, 1);
      this.mouthOpen = damp(this.mouthOpen, target, 22, delta);
    } else {
      this.mouthOpen = damp(this.mouthOpen, 0, 12, delta);
      if (this.mouthOpen < 0.005) this.mouthOpen = 0;
    }

    for (const { mesh, index } of this.avatar.mouth) {
      const influences = mesh.morphTargetInfluences;
      if (influences) influences[index] = this.mouthOpen;
    }
  }

  /** Cheap presence win the reference implementation left on the table. */
  private updateBlink(delta: number): void {
    if (this.avatar.blink.length === 0) return;

    if (this.blinkProgress >= 0) {
      this.blinkProgress += delta;
      const t = this.blinkProgress / BLINK_DURATION_SECONDS;
      // Up then down across the blink, so lids close and reopen in one arc.
      const influence = t >= 1 ? 0 : Math.sin(Math.min(t, 1) * Math.PI);
      // Every target moves together — a rig that splits left/right eyes must
      // not blink one at a time.
      for (const { mesh, index } of this.avatar.blink) {
        const influences = mesh.morphTargetInfluences;
        if (influences) influences[index] = influence;
      }
      if (t >= 1) this.blinkProgress = -1;
      return;
    }

    this.blinkTimer += delta;
    if (this.blinkTimer >= this.blinkInterval) {
      this.blinkTimer = 0;
      this.blinkInterval = this.nextBlinkInterval();
      this.blinkProgress = 0;
    }
  }

  /**
   * Yaw-only billboard. Flattening the target to the guide's own height keeps
   * it upright — a full `lookAt` tips the model when the user looks down at it.
   */
  private faceViewer(cameraPosition: Vector3): void {
    const scene = this.avatar.scene;
    scene.getWorldPosition(this.selfPosition);
    this.lookTarget.copy(cameraPosition);
    this.lookTarget.y = this.selfPosition.y;
    if (this.lookTarget.distanceToSquared(this.selfPosition) < 1e-6) return;
    scene.lookAt(this.lookTarget);
  }

  private findClipInsensitive(name: string) {
    const lowered = name.toLowerCase();
    for (const [key, clip] of this.avatar.clips) {
      if (key.toLowerCase() === lowered) return clip;
    }
    return undefined;
  }

  private nextIdleInterval(): number {
    const [min, max] = this.profile.clips.idleIntervalRange;
    return min + Math.random() * (max - min);
  }

  private nextBlinkInterval(): number {
    const [min, max] = this.profile.morphs.blinkIntervalRange;
    return min + Math.random() * (max - min);
  }
}
